import React from 'react';
import { MapPin, Battery, Clock, Navigation2, Phone, Wifi, Shield, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Location } from '../../types/database';

interface StationCardProps extends Location {
  distance?: number;
}

const StationCard: React.FC<StationCardProps> = ({
  name,
  address,
  distance
}) => {
  const { t } = useTranslation();

  const features = [
    { icon: <Battery className="text-primary-600" size={18} />, label: '4 Points de charge' },
    { icon: <Clock className="text-primary-600" size={18} />, label: '24/7' },
    { icon: <Wifi className="text-primary-600" size={18} />, label: 'Paiement sans contact' },
    { icon: <Shield className="text-primary-600" size={18} />, label: 'Site sécurisé' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg border border-gray-100">
      <div className="relative h-48">
        <img 
          src="https://images.pexels.com/photos/3819830/pexels-photo-3819830.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
          alt={name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
        {distance !== undefined && (
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-md">
            <span className="text-primary-600 font-medium">
              {distance < 1 ? `${(distance * 1000).toFixed(0)}m` : `${distance.toFixed(1)}km`}
            </span>
          </div>
        )}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-md">
            <div className="flex items-center text-primary-600 font-medium">
              <Battery className="mr-2" size={16} />
              <span>3/4 Bornes disponibles</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{name}</h3>
          <div className="flex items-center text-gray-600">
            <MapPin size={18} className="mr-2 flex-shrink-0" />
            <span className="text-sm">{address}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center">
              {feature.icon}
              <span className="text-sm ml-2">{feature.label}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <button 
            className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center justify-center font-medium"
          >
            <Navigation2 size={18} className="mr-2" />
            {t('stations.navigate')}
          </button>
          <button 
            className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center"
          >
            Détails
            <ChevronRight size={18} className="ml-1" />
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center text-gray-600">
              <Phone size={16} className="mr-1" />
              <span>Support 24/7</span>
            </div>
            <span className="text-green-600 font-medium flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
              En service
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StationCard;